import React from 'react';
import { Switch, Route, Redirect, NavLink, useRouteMatch, useHistory, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { TicketIcon, UserCircleIcon } from '@heroicons/react/24/outline';

import Booking from './Booking';
import Edit from './Edit';
import Review from './Review';
import PhoneConferm from './PhoneConferm';

import * as API from '../../api';
import { cn } from '../../shared/utils/cn';

export default function User() {
  const { t } = useTranslation();
  const history = useHistory();
  const account = '/account';
  const route = useRouteMatch();
  // Read fresh from localStorage rather than the module-level API.AuthStorage
  // snapshot — that constant is captured once when the JS bundle first
  // parses, so it stays stale/empty for the rest of the SPA session if the
  // user authenticates later (e.g. inline during checkout, same issue
  // thankyou.js already works around). Using the stale snapshot here caused
  // /account (and /account/orders) to always see AuthExist === false and
  // bounce straight back to "/".
  const auth = JSON.parse(localStorage.getItem('auth')) || {};
  const AuthExist = Object.keys(auth).length;

  React.useEffect(() => {
    if (!AuthExist) history.push('/');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [0]);

  if (!AuthExist) return null;

  const isUser = auth.user.type === 'user';

  // react-router-dom v5's NavLink takes a plain className string plus an
  // `activeClassName` prop — it does not support the v6 render-prop form
  // (`className={({isActive}) => ...}`), which silently fails to apply here.
  //
  // NavLink concatenates className + activeClassName into one attribute
  // rather than letting one override the other, so whichever utility comes
  // later in Tailwind's compiled stylesheet wins the cascade regardless of
  // attribute order — `bg-white` from the base class was beating
  // `bg-brand-500` from the active class, rendering white text on a white
  // pill. Using `!`-prefixed (important) utilities on the active class
  // guarantees it always wins.
  const tabClass = 'inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-semibold transition-colors bg-white text-ink hover:text-brand-500 border border-slate-200 sm:gap-2 sm:px-4 sm:py-2 sm:text-sm';
  const tabActiveClass = '!bg-brand-500 !text-white !border-brand-500 shadow-card';

  return (
    <div className="min-h-[60vh] bg-surface-muted pb-12">
      {/* Header */}
      <header className="border-b border-slate-100 bg-surface">
        <div className="mx-auto w-full max-w-container px-4 py-8 sm:px-6 lg:px-8">
          <h1 className="font-display text-2xl font-bold tracking-tight text-ink sm:text-3xl">
            {t('account.title')}
          </h1>
          <p className="mt-1 text-sm text-ink-muted">{t('account.subtitle')}</p>

          {isUser ? (
            <div className="mt-5 flex flex-nowrap gap-2">
              <NavLink
                exact
                to={`${account}/orders`}
                className={tabClass}
                activeClassName={tabActiveClass}
              >
                <TicketIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                {t('account.tabBookings')}
              </NavLink>
              <NavLink to={`${account}/edit`} className={tabClass} activeClassName={tabActiveClass}>
                <UserCircleIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                {t('account.tabDetails')}
              </NavLink>
            </div>
          ) : null}
        </div>
      </header>

      <div className="mx-auto w-full max-w-container px-4 py-8 sm:px-6 lg:px-8">
        {isUser ? (
          <Switch>
            <Route path={`${route.path}/orders`}>
              <Booking />
            </Route>
            <Route path={`${route.path}/edit`}>
              <Edit />
            </Route>
            <Route path={`${route.path}/review`}>
              <Review />
            </Route>
            <Route path={`${route.path}/phone-confirmation`}>
              <PhoneConferm />
            </Route>
            {/* Bare /account (and anything unmatched) lands on the bookings tab. */}
            <Redirect to={`${account}/orders`} />
          </Switch>
        ) : (
          <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-slate-200 bg-white px-6 py-20 text-center">
            <p className="text-ink-muted">{t('account.accessDenied')}</p>
            <Link
              to="/"
              className="mt-5 rounded-full bg-brand-500 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-brand-600"
            >
              {t('account.goHome')}
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
